import { Controller } from "@hotwired/stimulus"
import Chart from "chart.js/auto"

// Phase 2.5 Tier 4 — CIP Forecast stacked-area chart.
//
// Renders one filled band per fund source plus an "Unallocated" band so
// the stack always sums to the baseline total. Cumulative-only — the
// per-period bucket values live in the bucket table below the chart so
// the chart can stay honest about a single mode without a toggle.
export default class extends Controller {
  static values = { payload: Object }
  static targets = ["canvas", "legend"]

  connect() {
    this._render()
  }

  disconnect() {
    if (this._chart) {
      this._chart.destroy()
      this._chart = null
    }
  }

  _render() {
    const data = this.payloadValue
    if (!data || !Array.isArray(data.buckets) || data.buckets.length === 0) return

    const labels = data.buckets.map(iso => this._formatLabel(iso, data.grain))
    const palette = [
      "#4f46e5", "#059669", "#d97706", "#dc2626", "#7c3aed",
      "#0891b2", "#c026d3", "#65a30d", "#ea580c", "#0284c7"
    ]

    const datasets = (data.funds || []).map((fund, idx) => {
      const color = palette[idx % palette.length]
      return {
        label: fund.name,
        data: this._cumulative(fund.total),
        borderColor: color,
        backgroundColor: this._withAlpha(color, 0.5),
        fill: idx === 0 ? "origin" : "-1",
        tension: 0.2,
        pointRadius: 0,
        pointHoverRadius: 4,
        borderWidth: 1.5
      }
    })

    if (Array.isArray(data.unallocated_total) && data.unallocated_total.some(v => v > 0)) {
      datasets.push({
        label: "Unallocated",
        data: this._cumulative(data.unallocated_total),
        borderColor: "#94a3b8",
        backgroundColor: "rgba(148,163,184,0.4)",
        fill: datasets.length === 0 ? "origin" : "-1",
        tension: 0.2,
        pointRadius: 0,
        pointHoverRadius: 4,
        borderWidth: 1.5,
        borderDash: [4, 3]
      })
    }

    if (Array.isArray(data.baseline_total) && data.baseline_total.length) {
      datasets.push({
        label: "Total",
        data: this._cumulative(data.baseline_total),
        type: "line",
        borderColor: "#1f2937",
        backgroundColor: "transparent",
        fill: false,
        pointRadius: 0,
        pointHoverRadius: 5,
        borderWidth: 2.5,
        order: -1
      })
    }

    this._chart = new Chart(this.canvasTarget, {
      type: "line",
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 220 },
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: ctx => {
                const v = typeof ctx.parsed.y === "number" ? ctx.parsed.y : 0
                return `${ctx.dataset.label}: $${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
              }
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: val => `$${Number(val).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
            }
          }
        }
      }
    })

    this._renderLegend(datasets)
  }

  _renderLegend(datasets) {
    if (!this.hasLegendTarget) return

    const html = datasets.map(ds => `
      <span class="inline-flex items-center gap-1 text-gray-600">
        <span class="inline-block w-3 h-3 rounded-sm" style="background:${ds.borderColor}"></span>
        ${ds.label}
      </span>
    `).join("")
    this.legendTarget.innerHTML = html
  }

  _cumulative(arr) {
    let running = 0
    return (arr || []).map(v => {
      running += Number(v) || 0
      return running
    })
  }

  _withAlpha(hex, alpha) {
    const m = hex.replace("#", "").match(/.{2}/g) || []
    if (m.length !== 3) return `rgba(99,102,241,${alpha})`
    const [r, g, b] = m.map(s => parseInt(s, 16))
    return `rgba(${r},${g},${b},${alpha})`
  }

  _formatLabel(iso, grain) {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    const month = d.toLocaleString(undefined, { month: "short" })
    const year = d.getFullYear().toString().slice(2)
    if (grain === "yearly") return `${d.getFullYear()}`
    if (grain === "quarterly") {
      const q = Math.floor(d.getMonth() / 3) + 1
      return `Q${q} '${year}`
    }
    if (grain === "semi_annual") {
      return d.getMonth() < 6 ? `H1 '${year}` : `H2 '${year}`
    }
    return `${month} '${year}`
  }
}
